/**
 * Sign Shop Orders API — v2 contract (shadow: intrlock-shadow-sign-shop-api-v2)
 *
 * Rewritten §28.518.99.49.d.
 * On shadow: POST /v2/sign-shop/order-init → backend creates Stripe Checkout Session via
 * connector-api and returns hosted checkout URL. SPA redirects. Stripe hosted only —
 * no Stripe.js, no card fields (per standing rule 2026-08-19).
 *
 * On CES (app.intrlock.io) the legacy /stripe/sign-shop API stays live until Phase 3.
 */
import { apiOrigin } from '../utils/surface';

const IS_SHADOW =
  typeof window !== 'undefined' && window.location.hostname.startsWith('shadow.');

const V2_BASE = `${apiOrigin()}/v2/sign-shop`;

const ORDER_INIT_URL = `${V2_BASE}/order-init`;

const BRIDGE_API = IS_SHADOW
  ? 'https://zlj5y4dnz6.execute-api.us-east-1.amazonaws.com'
  : 'https://ovle12ewnh.execute-api.us-east-1.amazonaws.com';

const authHeaders = (): Record<string, string> => {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('intrlock_token')) || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...authHeaders(),
});

async function handle(res: Response): Promise<any> {
  if (!res.ok) {
    const body: any = await res.json().catch(() => ({}));
    const err = new Error(body?.error || body?.message || `${res.status} ${res.statusText}`);
    (err as any).status = res.status;
    (err as any).body = body;
    throw err;
  }
  return res.json();
}

// ── Bridge claim (unchanged legacy shim; kept for existing consumers) ───────

export const bridgeClaimApi = {
  activateByCode: (data: {
    activation_code: string;
    propertyId?: string | number;
    intercomId?: string | number;
    displayName?: string;
  }): Promise<{
    serial: string;
    role: string;
    has_cellular: boolean;
    device_profile_name?: string;
    propertyId: string | number | null;
    intercomId: string | number | null;
    displayName: string | null;
    activatedAt: string;
  }> => {
    return fetch(`${BRIDGE_API}/devices/activate-by-code`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    }).then(async r => {
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `${r.status}`);
      return r.json();
    });
  },
  provisioningKitUrls: (serial: string, installToken: string) => ({
    preset: `${BRIDGE_API}/preset/${serial}.json`,
    firstrun: `${BRIDGE_API}/firstrun/${serial}.sh?token=${encodeURIComponent(installToken)}`,
    installCommand: `curl -fsSL "${BRIDGE_API}/firstrun/${serial}.sh?token=${installToken}" | sudo bash`,
  }),
};

// ── Order types (v2) ────────────────────────────────────────────────────────

export interface SignOrderCartLine {
  design_id?: string;           // if this cart line references a saved design
  product_id: string;
  product_title: string;
  variant_id: number;
  variant_title: string;
  quantity: number;
  unit_price_cents: number;
  mockup_url?: string;
  property_id?: string | number;
  property_label?: string;
  intercom_id?: string | number;
  intercom_label?: string;
  // Legacy camelCase aliases (backend echoes both for now) — consumers can safely
  // read either. Remove once all render sites are migrated.
  productId?: string;
  productTitle?: string;
  variantId?: number;
  variantTitle?: string;
  unitPrice?: number;
  imageSrc?: string;
  propertyId?: string | number;
  propertyLabel?: string;
  intercomId?: string | number;
  intercomLabel?: string;
}

export interface SignOrderAddress {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  region: string;
  zip: string;
  country: string;
}

export type SignOrderStatus =
  | 'pending_payment'
  | 'paid_pending_approval'
  | 'approved'
  | 'in_production'
  | 'shipped'
  | 'delivered'
  | 'rejected'
  | 'approval_failed'
  | 'refunded'
  | 'canceled';

export interface SignOrderTracking {
  carrier?: string;
  number?: string;
  url?: string;              // printify-normalized URL (works for any carrier)
  status?: string;
  last_update?: string;
  events?: Array<{ ts: string; description: string; location?: string }>;
}

export interface SignOrder {
  id: string;
  org_id: string;
  user_id: string;
  status: SignOrderStatus;
  cart: SignOrderCartLine[];
  address: SignOrderAddress;
  customer_email: string;
  subtotal_cents: number;
  shipping_cents: number;
  tax_cents: number;
  total_cents: number;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  stripe_payment_status?: string;
  printify_order_id?: string;
  printify_error?: string;
  rejection_reason?: string;
  approval_notes?: string;
  tracking?: SignOrderTracking;
  tc_snapshot_id?: string;      // T&C version accepted at checkout
  created_at: string;
  paid_at?: string;
  approved_at?: string;
  rejected_at?: string;
  shipped_at?: string;
  delivered_at?: string;
}

// ── Orders API ──────────────────────────────────────────────────────────────

export const signShopOrdersApi = {
  /**
   * Order init — creates Stripe Checkout Session on backend, returns hosted URL.
   * SPA must window.location.assign(url) after this returns.
   *
   * NEVER call Stripe.js or handle card data. Standing rule 2026-08-19.
   */
  orderInit: (data: {
    cart: SignOrderCartLine[];
    address: SignOrderAddress;
    shipping_cents?: number;
    tc_accepted: boolean;      // T&C checkbox; backend snapshots the current T&C version
    return_origin?: string;
  }): Promise<{ order_id: string; checkout_url: string; session_id: string }> =>
    fetch(ORDER_INIT_URL, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    }).then(handle),

  /**
   * Read-back after Stripe redirects to success_url — resolves session_id → order.
   */
  syncSession: (session_id: string): Promise<SignOrder> =>
    fetch(`${V2_BASE}/orders/by-session/${encodeURIComponent(session_id)}`, {
      headers: authHeaders(),
    }).then(handle),

  listOrders: (): Promise<SignOrder[]> =>
    fetch(`${V2_BASE}/orders`, { headers: authHeaders() })
      .then(handle)
      .then((r: any) => (Array.isArray(r) ? r : r?.orders || [])),

  getOrder: (id: string): Promise<SignOrder> =>
    fetch(`${V2_BASE}/orders/${encodeURIComponent(id)}`, { headers: authHeaders() }).then(handle),

  /**
   * Refund is admin-only server-side; SPA just triggers, backend enforces role.
   */
  refund: (id: string, amount_cents?: number, reason?: string): Promise<SignOrder> =>
    fetch(`${V2_BASE}/orders/${encodeURIComponent(id)}/refund`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ amount_cents, reason: reason || '' }),
    }).then(handle),

  /**
   * Reprint — creates a new Printify order from the same design at no charge.
   * Admin-only server-side.
   */
  reprint: (id: string, reason: string): Promise<SignOrder> =>
    fetch(`${V2_BASE}/orders/${encodeURIComponent(id)}/reprint`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ reason }),
    }).then(handle),

  addNote: (id: string, note: string): Promise<{ ok: true }> =>
    fetch(`${V2_BASE}/orders/${encodeURIComponent(id)}/notes`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ note }),
    }).then(handle),

  // ── Backward-compat shims (legacy CES + pre-v2 SPA callers) ──────────────
  // These preserve the old signatures used by SignShop.tsx and SignShopOrders.tsx
  // so we don't have to rewrite those files in the same landing. They proxy to v2
  // endpoints on shadow and to the legacy /stripe/sign-shop routes on CES.

  createCheckoutSession: (data: {
    cart: any[];
    address: SignOrderAddress;
    shipping_cents?: number;
    return_origin?: string;
    tc_accepted?: boolean;
  }): Promise<{ order_id: string; url: string; session_id: string }> => {
    // Legacy shape used camelCase (variantId, unitPrice) vs new snake_case.
    const normalized = (data.cart || []).map((c: any) => ({
      design_id: c.design_id || c.designId,
      product_id: c.product_id || c.productId,
      product_title: c.product_title || c.productTitle,
      variant_id: c.variant_id ?? c.variantId,
      variant_title: c.variant_title || c.variantTitle,
      quantity: c.quantity,
      unit_price_cents: c.unit_price_cents ?? c.unitPrice,
      mockup_url: c.mockup_url || c.imageSrc,
      property_id: c.property_id ?? c.propertyId,
      property_label: c.property_label || c.propertyLabel,
      intercom_id: c.intercom_id ?? c.intercomId,
      intercom_label: c.intercom_label || c.intercomLabel,
    }));
    if (IS_SHADOW) {
      return fetch(ORDER_INIT_URL, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({
          cart: normalized,
          address: data.address,
          tc_accepted: data.tc_accepted ?? true,
          return_origin: data.return_origin,
        }),
      }).then(handle).then((r: any) => ({ order_id: r.order_id, url: r.checkout_url || r.url, session_id: r.session_id }));
    }
    return fetch(`${V2_BASE}/checkout-session`, {
      method: 'POST', headers: jsonHeaders(), body: JSON.stringify(data),
    }).then(handle);
  },

  personalize: (data: {
    base_product_id: string;
    variant_id: number;
    personalization_mode: 'quick' | 'custom';
    image_base64?: string;
    property_label?: string;
    address_label?: string;
    phone?: string;
    qr_target?: string;
  }): Promise<{
    personalized_product_id: string;
    variant_id: number;
    price_cents: number;
    mockup_urls: string[];
    mockups: Array<{ url: string; label: string; is_default: boolean; camera: string }>;
    upload_id: string;
  }> => {
    // On shadow, /personalize is folded into /preview but preserves the legacy shape
    // for now via a compat endpoint. Backend Chunk 5 keeps /personalize as an alias.
    return fetch(`${V2_BASE}/personalize`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(data),
    }).then(handle);
  },

  // Legacy admin-approve/reject shims — routed to admin endpoints on shadow.
  approve: (id: string, notes?: string): Promise<SignOrder> => {
    const base = IS_SHADOW
      ? 'https://zlj5y4dnz6.execute-api.us-east-1.amazonaws.com/admin/sign-shop'
      : `${apiOrigin()}/stripe/admin/sign-shop`;
    return fetch(`${base}/orders/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ notes: notes || '' }),
    }).then(handle);
  },

  reject: (id: string, reason: string): Promise<SignOrder> => {
    const base = IS_SHADOW
      ? 'https://zlj5y4dnz6.execute-api.us-east-1.amazonaws.com/admin/sign-shop'
      : `${apiOrigin()}/stripe/admin/sign-shop`;
    return fetch(`${base}/orders/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ reason }),
    }).then(handle);
  },
};
